import d3 = require("d3");
import React = require("react");
import ReactFauxDOM = require("react-faux-dom");
import {v4 as uuid} from 'uuid';
import {LegendItem} from "../../../components/charts/Legend";
import {IconSize} from "./PieChartWithLabels";
import "./style/StackedBarChart.scss";
import {FormatLocaleNumber} from "../DashboardUtils";

interface Margin {
    top: number;
    bottom: number;
    left: number;
    right: number;
}

export interface AxesVisibility {
    x: { line: boolean, tickLine: boolean };
    y: { line: boolean, tickLine: boolean };
}

interface LegendOption {
    external: boolean;
    placeOn?: string;
    showIcon?: boolean;
    showName?: boolean;
    iconSize?: IconSize;
    sizeRatio?: number;
    margin?: Margin;
    order?: string;
    numberColumns?: number; // define how many columns use to place items in multiple lines
}

export interface StackedBarChartProps {
    width: number;
    height: number;
    direction: string;
    showValuesLabels: boolean;
    addTotalsItem: boolean;
    data: StackedBarChartRow[];
    legendItems: LegendItem[];
    legendOptions: LegendOption;
    margin: Margin;
    texts: any; // FIXME???,
    showAxesOptions: AxesVisibility;
    selectedItem?: string;
    onPathOver?: Function;
    onClick?: Function;
    order?: string;
}

export interface StackedBarChartSubItem {
    label: string;
    code: string;
    value: number;
    icon?: string;
    color?: string;
}

export interface StackedBarChartRow {
    label: string;
    subItems: StackedBarChartSubItem[];
}

interface OptimizedData {
    domain: string[];
    totals: number[];
    dataDenormalized: any;
}

/**
 * This component draw a stacked bar chart
 * It's possible to choose:
 *  - direction of the chart(horizontal or vertical)
 *  - axes and ticks visibility
 *  - labels visibility
 *  - totals of each serie visibility
 *  - external or internal legend
 *
 */
export class StackedBarChart extends React.Component<StackedBarChartProps, {}> {

    public static getLegendItemColor: Function = (itemCode: string, legendItems: LegendItem[]): string => {
        const item = legendItems.filter((e) => e.code === itemCode)[0];

        if (item != null) {
            return item.color;
        }
        return StackedBarChart.DEFAULT_COLOR;
    }

    /**
     * Given a array of sub items and a list of item codes to aggregate, return a new array of sub items
     * where item to aggregate have been replaced by a single other item that is the sum of them.
     *
     * @param subItems             the original array of subitems
     * @param itemsGuidToAggregate the codes of items to aggregate
     * @param texts                the text to compose the other sub item
     * @param otherItem            the other item used in the external legend (if present)
     */
    public static reduceSubItemsVisible(
        subItems: StackedBarChartSubItem[],
        itemsGuidToAggregate: string[],
        texts: any,
        otherItem?: LegendItem): StackedBarChartSubItem[] {

        const subItemsToAggregate: StackedBarChartSubItem[] = subItems.filter(
            (si) => itemsGuidToAggregate.includes(si.code, 0),
        );

        const subItemOthers: StackedBarChartSubItem = {
            label: texts.others,
            code: otherItem == null ? uuid() : otherItem.code,
            value: subItemsToAggregate.reduce((a, b) => a + b.value, 0),
            color: otherItem == null ? otherItem.color : "dimgrey",
        };
        let newSubItems: StackedBarChartSubItem[];
        newSubItems = subItems.filter((si) => !itemsGuidToAggregate.includes(si.code, 0));
        newSubItems.push(subItemOthers);
        return newSubItems;
    }

    /**
     * Given an array of levels data and a maximum number of level to show,
     * reduce exceeding levels into a single aggregated level called Others
     * @param data
     * @param maxNumber
     * @param texts
     */
    public static reduceBars(data: StackedBarChartRow[], maxNumber, texts): StackedBarChartRow[] {
        data.sort(
            (a, b) =>
                b.subItems.reduce((p, c) => p + c.value, 0) - a.subItems.reduce((p, c) => p + c.value, 0),
        );
        if (data.length <= maxNumber) {
            return data;
        }
        const levelsToAggregate = data.slice(maxNumber - 1, data.length - 1);
        const othersLevelAlarmTypes: Map<string, StackedBarChartSubItem> = new Map<string, StackedBarChartSubItem>();
        levelsToAggregate.forEach((lta) => {
            lta.subItems.forEach((si) => {
                const currentSubItem = othersLevelAlarmTypes.get(si.code);
                if (currentSubItem == null) {
                    othersLevelAlarmTypes.set(si.code, si);
                } else {
                    currentSubItem.value += si.value;
                    othersLevelAlarmTypes.set(si.code, currentSubItem);
                }
            });
        });

        const othersBar: StackedBarChartRow = {
            label: texts.others,
            subItems: Array.from(othersLevelAlarmTypes.values()),
        };

        data = data.slice(0, maxNumber - 1);
        data.push(othersBar);
        return data;
    }

    private static DEFAULT_COLOR: string = "var(--vebox-gray-hover-light)";
    private static SELECTION_COLOR: string = "var(--danfoss-web-grey)";

    public svgElement;

    constructor(props: StackedBarChartProps) {
        super(props);
        this.mouseOut = this.mouseOut.bind(this);
        this.mouseOver = this.mouseOver.bind(this);
        this.svgElement = React.createRef();
    }

    /*refCallback = element => {
        if (element) {
            this.hideLabelOnSmallBar(element);
            this.ellipsisLegendItem(element);
        }
    }*/

    public getRef = (svgElement) => this.svgElement = svgElement;

    public mouseOver(event, itemData) {
        if (this.props.legendOptions.external) {
            this.props.onPathOver(itemData.key);
        }
    }

    public mouseOut() {
        if (this.props.legendOptions.external) {
            this.props.onPathOver(null);
        }
    }

    public dataOptimized: Function = (originalData: StackedBarChartRow[]): OptimizedData => {
        // let domainValues = originalData.map(d => d.label);
        const totals = originalData.map(
            (d) => d.subItems.map((si) => si.value)
                .reduce((a, b) => a + b),
        );

        let dataDenormalized = [];
        let subItemsTotals;
        let itemTotals;
        let totalEquipment: number = 0;

        // compute totals by subItem and denormalized data for stack layout
        originalData.forEach((d, i) => {
                const tmpObj = {label: d.label, total: null};
                if (subItemsTotals === undefined) {
                    subItemsTotals = {label: this.props.texts.totals};
                }
                d.subItems.forEach((e) => {
                    tmpObj[e.code] = e.value;
                    totalEquipment += e.value;
                    if (subItemsTotals[e.code] === undefined) {
                        subItemsTotals[e.code] = e.value;
                    } else {
                        subItemsTotals[e.code] += e.value;

                    }
                });
                if (itemTotals === undefined) {
                    itemTotals = {};
                }

                // add 0 value where missing
                this.props.legendItems.forEach((li) => {
                    if (Object.keys(tmpObj).indexOf(li.code) === -1) {
                        tmpObj[li.code] = 0;
                    } else {
                        if (itemTotals[d.label] === undefined) {
                            itemTotals[d.label] = 0;
                        }
                        itemTotals[d.label] += tmpObj[li.code];
                    }
                });
                tmpObj.total = itemTotals[d.label];
                dataDenormalized.push(tmpObj);
            },
        );

        // orders items in decrementing way
        if (this.props.order !== "custom") {
            dataDenormalized = dataDenormalized.sort((a, b) => {
                return (b.total - a.total);
            });
        }

        // retrieve items domain
        const domainValues = dataDenormalized.map((d) => d.label);

        // manage add totals item option
        if (this.props.addTotalsItem) {
            dataDenormalized.push(subItemsTotals);
            totals.push(totalEquipment);
            domainValues.push(this.props.texts.totals);
        }

        return {
            domain: domainValues,
            totals,
            dataDenormalized,
        };
    }

    public selectItem(div: ReactFauxDOM) {
        const svg = d3.select(div).select("svg")
            .attr("ref", this.svgElement);
        if (this.props.selectedItem != null) {
            svg.selectAll("[data-key=" + this.props.selectedItem + "]")
                .style("fill", StackedBarChart.SELECTION_COLOR);
        }
    }

    public render() {
        const div = new ReactFauxDOM.Element("div");
        if (this.props.data.length === 0) {
            d3.select(div)
                .append("p")
                .attr("style", "line-height: " + this.props.height + "px") // vertical align the text into the widget
                .text(this.props.texts.no_data_message);

            return div.toReact();
        }
        const optimizedData: OptimizedData = this.dataOptimized(this.props.data);

        /* define and compute elements dimensions */
        const circleSize = this.props.width / 75;

        // used to distance item one from each other
        const legendItemMargin: Margin = {
            top: 5,
            bottom: 0,
            left: 5,
            right: 0,
        };

        const firstItemMargin = 20;

        // compute the width and height of a legend item  margin excluded
        let itemWidth = this.props.width;
        let itemHeight = this.props.height;
        let legendHeight = 0;
        let computedChartWidth = this.props.width - this.props.margin.left - this.props.margin.right;
        let computedChartHeight = this.props.height - this.props.margin.top - this.props.margin.bottom;

        // define the number of column to use to dispose legend items if it is not external
        let columnNumber = 0;
        if (!this.props.legendOptions.external) {
            columnNumber = Math.min(this.props.legendItems.length, (this.props.legendOptions.numberColumns || 3));
            if (this.props.legendOptions.placeOn === "top") {
                itemWidth = (this.props.width - firstItemMargin - legendItemMargin.left * this.props.legendItems.length - 1) / columnNumber;
            } else {
                itemWidth = (this.props.width * this.props.legendOptions.sizeRatio);
            }
            itemHeight = Math.max(circleSize * 2,
                (this.props.legendOptions.showIcon ? this.props.legendOptions.iconSize.height : 0));

            // compute the total height of the legend
            if (this.props.legendOptions.placeOn === "top") {
                const legendRowsNum = Math.floor((this.props.legendItems.length - 1) / columnNumber);
                legendHeight = itemHeight * Math.round(
                    this.props.legendItems.length / columnNumber) + (legendRowsNum * legendItemMargin.top);
            } else {
                legendHeight = this.props.height;
            }
            // compute the remaining width and height for draw the chart
            if (this.props.legendOptions.placeOn === "left") {
                computedChartWidth = this.props.width * (1 - this.props.legendOptions.sizeRatio);
                computedChartHeight = this.props.height;
            } else {
                computedChartHeight -= legendHeight;
            }
        }

        const chartSize = {
            width: computedChartWidth,
            height: computedChartHeight,
        };

        const barsPadding: Margin = {
            top: 10,
            bottom: 20,
            left: 10,
            right: 10,
        };

        // ELEMENTS DEFINITION
        const svg = d3.select(div)
            .attr("className", "stacked-bar-chart__graph")
            .append("svg")
            .attr("width", this.props.width)
            .attr("height", this.props.height);

        // LEGEND
        const items = this.props.legendItems;
        if (!this.props.legendOptions.external) {
            const legendItems = svg.append("g")
                .attr("className", "stacked-bar-chart-widget__legend")

                .attr("transform",
                    "translate(0," + (this.props.legendOptions.margin.top) + ")")
                .selectAll("g")
                .data(items)
                .enter()
                .append("g")
                .attr("className", "stacked-bar-chart-widget__legend__item");

            switch (this.props.legendOptions.placeOn) {
                case "top": {
                    // allign items into rows and columns applying also margins
                    legendItems.attr("transform", function (d, i) {
                        return "translate(" + (firstItemMargin + legendItemMargin.left * (i % columnNumber) + itemWidth * (i % columnNumber))
                            + ","
                            + (itemHeight * Math.floor(i / columnNumber) + (Math.floor(
                                i / columnNumber) === 0 ? 0 : legendItemMargin.top * (i / columnNumber)))
                            + " )";
                    });
                    break;
                }
                case "left": {
                    legendItems.attr("transform", (d, i) => {
                        return "translate(" + circleSize + "," + itemHeight * i + ")";
                    });
                    break;
                }
            }

            legendItems.append("circle")
                .attr("className", "stacked-bar-chart-widget__legend__item__color")
                .attr("r", circleSize)
                .attr("cx", 5)
                .attr("cy",
                    this.props.legendOptions.showIcon ? this.props.legendOptions.iconSize.height / 2 + 1 : circleSize + 1)
                .attr("fill", (d) => {
                    return StackedBarChart.getLegendItemColor(d.code, items);
                });

            if (this.props.legendOptions.showIcon) {
                const legendTooltips = legendItems.append("g")
                    .attr("className", "stacked-bar-chart-widget__legend__item__tooltip")
                    .attr("style", "visibility:hidden");
                legendTooltips.append("rect")
                    .attr("width", 10)
                    .attr("height", 10)
                    .attr("fill", "red");
                legendTooltips.append("text")
                    .data(items)
                    .text((d) => {
                        return d.name;
                    });

                legendItems.filter((li) => (li.icon !== undefined && li.icon !== "" && li.icon !== null))
                    .append("image")
                    .attr("xlink:href", function (d) {
                        return d.icon;
                    })
                    .attr("width", this.props.legendOptions.iconSize.width)
                    .attr("height", this.props.legendOptions.iconSize.height)
                    .attr("transform", "translate(" + (circleSize + 10) + ", 0)");
            }
            if (this.props.legendOptions.showName) {
                // used to distance item label from the circle or icon
                const legendItemLeftPadding = 5;

                const textItemXPos = (
                    circleSize + legendItemLeftPadding +
                    (this.props.legendOptions.showIcon ? this.props.legendOptions.iconSize.width : 0) +
                    legendItemLeftPadding
                );
                legendItems.append("text")
                    .attr("className", "stacked-bar-chart-widget__legend__item__name")
                    .text((d, i) => {
                        if (d.name.length >= 22) {
                            return d.name.substring(0, 18) + "...";
                        }
                        return d.name;
                    })
                    .attr("dy", "0.30em")
                    .attr("transform",
                        "translate(" + textItemXPos + ", " + (this.props.legendOptions.showIcon ? this.props.legendOptions.iconSize.height / 2 : circleSize) + ")");

                legendItems.append("text")
                    .attr("className", "stacked-bar-chart-widget__legend__item__name--ellipsis")
                    .attr("style", "visibility: hidden")
                    .text((d, i) => {
                        return d.name.substring(0, d.name.length / 2) + "...";
                    })
                    .attr("dy", "0.30em")
                    .attr("transform",
                        "translate(" + textItemXPos + ", " + (this.props.legendOptions.showIcon ? this.props.legendOptions.iconSize.height / 2 : circleSize) + ")");

            }
        }

        // chart block
        const chartG = svg.append("g")
            .attr("className", "stacked-bar-chart-widget__chart")
            .attr("transform", (d) => {
                if (this.props.legendOptions.placeOn === "left") {
                    return "translate(" +
                        this.props.width * this.props.legendOptions.sizeRatio +
                        "," +
                        this.props.legendOptions.margin.top +
                        ")";
                }
                return "translate(" +
                    this.props.margin.left +
                    "," +
                    // vertical center the chart into the remaining space
                    ((legendHeight + (this.props.height - legendHeight - computedChartHeight) / 2) + 20) +
                    ")";
            });

        // BARS
        const barsG = chartG.append("g")
            .attr("className", "stacked-bar-chart-widget__bars");

        // LABELS
        const labelsG = chartG.append("g")
            .attr("className", "stacked-bar-chart-widget__labels");

        /* AXES DEFINITION */
        let xAxis;
        let yAxis;

        if (this.props.direction === "vertical") {
            xAxis = d3.scaleBand()
                .domain(optimizedData.domain)
                .rangeRound([0, chartSize.width - barsPadding.right - barsPadding.left])
                .paddingInner(0.05)
                .align(0.1);

            yAxis = d3.scaleLinear()
                .domain([0, Math.max.apply(Math, optimizedData.totals)])
                .rangeRound([chartSize.height - barsPadding.top - barsPadding.bottom, 0])
                .ticks(4);
        } else {
            xAxis = d3.scaleLinear()
                .domain([0, Math.max.apply(Math, optimizedData.totals)])
                .rangeRound([0, chartSize.width - barsPadding.right - barsPadding.left]);

            let paddingOuter;
            let paddingInner;
            const minBarMargin = 20;
            if (optimizedData.domain.length > 1) {
                paddingOuter = Math.max(
                    (minBarMargin * optimizedData.domain.length) / (chartSize.height - barsPadding.top - barsPadding.bottom),
                    0.10,
                );
                paddingInner = Math.max(
                    (minBarMargin * optimizedData.domain.length) / (chartSize.height - barsPadding.top - barsPadding.bottom),
                    0.20,
                );
            } else {
                // use a greater padding if there is only an element
                paddingInner = 0.5;
                paddingOuter = minBarMargin / (chartSize.height - barsPadding.top - barsPadding.bottom);
            }
            yAxis = d3.scaleBand()
                .domain(optimizedData.domain)
                .rangeRound([0, chartSize.height - barsPadding.top - barsPadding.bottom])
                .paddingInner(paddingInner)
                .paddingOuter(paddingOuter)
                .align(0.1);
        }

        // AXES

        const xAxisG = chartG.append("g")
            .attr("className", "stacked-bar-chart-widget__xaxis")
            // .attr("transform", "translate(0," + (chartSize.height - barsPadding.bottom -barsPadding.top) + ")")
            .attr("transform", "translate(0," + optimizedData.domain.length * (20 + yAxis.bandwidth()) + ")");
        const yAxisG = chartG.append("g")
            .attr("className", "stacked-bar-chart-widget__yaxis")
            .attr("transform", "translate(0,0)");

        /* DEFINE THE STACK LAYOUT USING THE COLOR OF SUB ITEMS AS KEY */
        const stack = d3.stack()
            .keys(this.props.legendItems.map((d) => {
                return d.code;
            }))
            .order(d3.stackOrderAscending);

        const series = stack(optimizedData.dataDenormalized);

        const onClick = this.onClick;

        /* DRAW BARS USING THE DATA SERIES PRODUCED BY THE STACK LAYOUT */
        const barsRects = barsG
            .attr("transform", "translate(0,0)")
            .selectAll("g")
            .data(series)
            .enter()
            .append("g")
            .attr("data-index", (d, i) => {
                return i;
            })
            .attr("data-key", (d: any) => {
                return d.key;
            })
            .style("fill", (d) => {
                return StackedBarChart.getLegendItemColor(d.key, items);
            })
            .on("mouseover", this.mouseOver)
            .on("mouseout", this.mouseOut)
            .selectAll("rect")
            .data(function (d) {
                return d;
            })
            .enter().append("rect")
            .attr("data-id", function (d, i) {
                const parentElementIndex = (this as any).parentNode.getAttribute("data-index");
                return "rect_" + parentElementIndex + "_" + i;
            })
            .style("cursor", "pointer")
            .on("click", function (event, d) {
                const key = (this as any).parentNode.getAttribute("data-key");
                onClick({
                    x: d.data.label,
                    y: key,
                })
            });

        /* DEFINE POSITION AND SIZE OF EACH BARS */
        barsRects
            .attr("x", (d) => {
                if (this.props.direction === "horizontal") {
                    return xAxis(d[0]);
                }
                return xAxis(d.data.label);
            })
            .attr("y", (d) => {
                if (this.props.direction === "horizontal") {
                    return yAxis(d.data.label);
                }
                return yAxis(d[1]);
            })
            .attr("height", (d) => {
                if (this.props.direction === "horizontal") {
                    return yAxis.bandwidth();
                }
                return yAxis(d[0]) - yAxis(d[1]);
            })
            .attr("width", (d) => {
                if (this.props.direction === "horizontal") {
                    return xAxis(d[1]) - xAxis(d[0]);
                }
                return xAxis.bandwidth();
            });

        /* MANAGE LABELS */
        if (this.props.showValuesLabels) {
            labelsG.selectAll("g")
                .data(series)
                .enter()
                .append("g") // add a group of labels for each serie element
                .attr("data-index", (d, i) => {
                    return i;
                })
                .selectAll("text")
                .data(function (d) {
                    return d;
                })
                .enter()
                .append("text")
                .attr("data-id", function (d, i) {
                    const parentElementIndex = (this as any).parentNode.getAttribute("data-index");
                    return "label_" + parentElementIndex + "_" + i;
                })
                .attr("className", "stacked-bar-chart-widget__label")
                .text((d) => {
                    return FormatLocaleNumber(d[1] - d[0]);
                })
                // place the label in the middle of the rect it refers to
                .attr("dy", "0.35em")
                .attr("transform", (d) => {
                        if (this.props.direction === "horizontal") {
                            return "translate(" +
                                // x: the left border position of the rect plus the half of its width
                                xAxis((d[0] + (d[1] - d[0]) / 2)) + "," +
                                // y: the top border of the rect plus the half of its height
                                (yAxis(d.data.label) + yAxis.bandwidth() / 2) +
                                ")";
                        } else {
                            return "translate(" +
                                // x: the left border of the rect plus the half of its width
                                (xAxis(d.data.label) + xAxis.bandwidth() / 2) + "," +
                                // y: the top border position of the rect plus the half of its height
                                yAxis(d[0] + (d[1] - d[0]) / 2) +
                                ")";
                        }
                    },
                );
        }

        /* DRAW AXES AND CUSTOMIZE THEIRS VISIBILITY */
        const maxNum = Math.max.apply(Math, optimizedData.totals);
        const numTicks = maxNum < 5 ? maxNum : 5;
        const x_axis = d3.axisBottom(xAxis)
            .ticks([numTicks]);

        if (this.props.direction === "horizontal") {
            x_axis.tickFormat(d3.format("d"));
        }

        xAxisG.call(x_axis.tickFormat((d): any =>
            typeof d === 'number' ? FormatLocaleNumber(d as number) : d))
            .select(".domain") // show or hide x axis line
            .style("visibility", () => {
                if (this.props.showAxesOptions.x.line) {
                    return "";
                }
                return "hidden";
            });
        xAxisG.call(x_axis.tickFormat((d): any =>
            typeof d === 'number' ? FormatLocaleNumber(d as number) : d))
            .selectAll(".tick") // show or hide the line of x axis ticks
            .selectAll("line")
            .style("visibility", () => {
                if (this.props.showAxesOptions.x.tickLine) {
                    return "";
                }
                return "hidden";
            });

        const y_axis = d3.axisLeft(yAxis);

        if (this.props.direction === "vertical") {
            y_axis.tickFormat(d3.format("d"));
        }

        yAxisG.call(y_axis)
            .select(".domain")  // show or hide the x axis line
            .style("visibility", () => {
                if (this.props.showAxesOptions.y.line) {
                    return "";
                }
                return "hidden";
            });

        yAxisG.call(y_axis)
            .selectAll(".tick") // show or hide the line of y axis ticks
            .selectAll("line")
            .style("visibility", () => {
                if (this.props.showAxesOptions.y.tickLine) {
                    return "";
                }
                return "hidden";
            });

        // place element labels above the bar
        yAxisG.selectAll(".tick")
            .selectAll("text")
            .attr("text-anchor", "start")
            .attr("dy", 0)
            .attr("x", -15)
            .attr("y", function () {
                return -yAxis.bandwidth() / 2 - 5;
            });

        this.selectItem(div);
        return div.toReact();
    }

    /* hide all labels that are larger than the rect the the refer to*/
    public hideLabelOnSmallBar(svg: SVGElement) {
        d3.select(svg).selectAll(".stacked-bar-chart-widget__label")
            .style("visibility", function () {
                const labelDataId: string = (this as SVGTextElement).getAttribute("data-id");
                const labelParentIndex = ((this as SVGTextElement).parentNode as any).getAttribute("data-index"); // WARNING: cannot use parentElement is undefined in IE11
                const labelId = labelDataId.substring(labelDataId.lastIndexOf("_") + 1, labelDataId.length);
                const rectOfLabel = d3.select(svg).select("[data-id = rect_" + labelParentIndex + "_" + labelId);
                const rectOfLabelNode: any = rectOfLabel.node();
                if (rectOfLabelNode == null
                    || rectOfLabelNode.getAttribute("width") < (this as any).getComputedTextLength()
                    || rectOfLabelNode.getAttribute("height") < (this as any).getBBox().height
                ) {
                    return "hidden";
                }
                return "";
            });
    }

    public ellipsisLegendItem(svg: SVGElement) {
        const _ref = this;
        d3.select(svg).selectAll(".stacked-bar-chart-widget__legend__item")
            .selectAll(".stacked-bar-chart-widget__legend__item__name")
            .attr("style", function (d, i) {
                const realItemWidth = (this as any).getBoundingClientRect().right - (this as any).getBoundingClientRect().left;
                if (realItemWidth > (_ref.props.width / Math.min(_ref.props.legendItems.length,
                    (_ref.props.legendOptions.numberColumns || 3)))) {
                    return "visibility: hidden";
                } else {
                    return "visibility: visible";
                }
            });

        d3.select(svg).selectAll(".stacked-bar-chart-widget__legend__item")
            .selectAll(".stacked-bar-chart-widget__legend__item__name--ellipsis")
            .attr("style", function (d, i) {
                const realItemWidth = (this as any).getBoundingClientRect().right - (this as any).getBoundingClientRect().left;
                if (realItemWidth > (_ref.props.width / Math.min(_ref.props.legendItems.length,
                    (_ref.props.legendOptions.numberColumns || 3)))) {
                    return "visibility: visible";
                } else {
                    return "visibility: hidden";
                }
            });
    }

    public componentDidMount() {
        this.hideLabelOnSmallBar(this.svgElement.current);
        this.ellipsisLegendItem(this.svgElement.current);
    }

    private onClick = (itemData) => {
        if (this.props.onClick) {
            this.props.onClick(itemData);
        }
    }
}
